import { DatePicker, Input, Tag } from "antd";
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { AiOutlinePlus, AiOutlineClose } from "react-icons/ai";
import dayjs from "dayjs";
import CommonButton from "../common/Button/CommonButton";
import ActionsModal from "../common/Modals/ActionsModal";
import { BackIcon } from "../Icons/BackIcon";
import { predictionValidationSchema, MAX_OPTION_LENGTH } from "../../utils/validation";

const PredictionForm = ({
  onSubmit,
  onCancel,
  onPublish,
  initialData = null,
  loading = false,
  publishing = false,
}) => {
  const isEdit = !!initialData;
  const [tagInput, setTagInput] = useState("");
  const [publishModal, setPublishModal] = useState(false);
  const [pendingPublishPayload, setPendingPublishPayload] = useState(null);
  const [optionsValidationAttempted, setOptionsValidationAttempted] =
    useState(false);

  const formik = useFormik({
    initialValues: {
      question: initialData?.question || "",
      subtitle: initialData?.subtitle || "",
      tags: initialData?.tags?.join(", ") || "",
      end_date: initialData?.end_date ? new Date(initialData.end_date) : null,
      options: initialData?.options?.map((o) => (typeof o === "string" ? o : o.label)) || [
        "",
        "",
      ],
    },
    validationSchema: predictionValidationSchema,
    onSubmit: (values) => {
      const payload = {
        question: values.question.trim(),
        subtitle: values.subtitle?.trim() || "",
        tags: tags,
        end_date: values.end_date
          ? new Date(values.end_date).toISOString()
          : null,
        options: values.options.filter((opt) => opt.trim() !== ""),
      };
      onSubmit(payload);
    },
  });

  const [tags, setTags] = useState(initialData?.tags || []);

  useEffect(() => {
    formik.setFieldValue("tags", tags.join(", "));
    // eslint-disable-next-line
  }, [tags]);

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (removedTag) => {
    setTags(tags.filter((tag) => tag !== removedTag));
  };

  const MAX_OPTIONS = 5;

  const handleAddOption = () => {
    if (formik.values.options.length >= MAX_OPTIONS) return;
    formik.setFieldValue("options", [...formik.values.options, ""]);
  };

  const handleRemoveOption = (index) => {
    if (formik.values.options.length <= 2) return;
    const updated = formik.values.options.filter((_, i) => i !== index);
    formik.setFieldValue("options", updated);
  };

  const handleOptionChange = (index, value) => {
    const updated = [...formik.values.options];
    updated[index] = value;
    formik.setFieldValue("options", updated);
  };

  const isOptionDuplicate = (index, option) =>
    option.trim() !== "" &&
    formik.values.options.some(
      (o, i) =>
        i !== index && o.trim().toLowerCase() === option.trim().toLowerCase()
    );

  const getOptionInputStatus = (index, option) => {
    if (!optionsValidationAttempted) {
      return "";
    }

    if (isOptionDuplicate(index, option)) {
      return "error";
    }

    if (!option.trim()) return "error";
    if (
      Array.isArray(formik.errors.options) &&
      formik.errors.options[index]
    ) {
      return "error";
    }

    return "";
  };

  const hasDuplicateOptions = (options) => {
    const trimmed = options
      .map((o) => o?.trim().toLowerCase())
      .filter(Boolean);
    return new Set(trimmed).size !== trimmed.length;
  };

  const markFormErrorsTouched = (errors) => {
    formik.setErrors(errors);
    formik.setTouched(
      {
        question: true,
        subtitle: true,
        tags: true,
        end_date: true,
        options: true,
      },
      false
    );
  };

  const getOptionsErrorMessage = () => {
    const optionsError = formik.errors.options;
    if (!optionsError) return null;
    if (typeof optionsError === "string") return optionsError;
    if (hasDuplicateOptions(formik.values.options)) {
      return "Duplicate options are not allowed";
    }
    return "Please fill all option fields";
  };

  const shouldShowOptionsError =
    optionsValidationAttempted &&
    Boolean(formik.errors.options || hasDuplicateOptions(formik.values.options));

  const buildPayload = (values) => ({
    question: values.question.trim(),
    subtitle: values.subtitle?.trim() || "",
    tags: tags,
    end_date: values.end_date
      ? new Date(values.end_date).toISOString()
      : null,
    options: values.options.filter((opt) => opt.trim() !== ""),
  });

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    setOptionsValidationAttempted(true);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(buildPayload(formik.values));
    } else {
      markFormErrorsTouched(errors);
      formik.setSubmitting(false);
    }
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    setOptionsValidationAttempted(true);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      setPendingPublishPayload(buildPayload(formik.values));
      setPublishModal(true);
    } else {
      markFormErrorsTouched(errors);
    }
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <span className="cursor-pointer mr-3" onClick={onCancel}>
          <BackIcon />
        </span>
        <h2 className="text-2xl font-semibold font-poppins m-0">
          {isEdit ? "Edit Prediction" : "Create Prediction"}
        </h2>
      </div>

      <form onSubmit={handleSaveDraft}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Question */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question <span className="text-red-500">*</span>
            </label>
            <Input.TextArea
              name="question"
              value={formik.values.question}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter prediction question"
              rows={2}
              className="w-full"
            />
            {formik.touched.question && formik.errors.question && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.question}
              </p>
            )}
          </div>

          {/* Subtitle */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subtitle <span className="text-red-500">*</span>
            </label>
            <Input
              name="subtitle"
              value={formik.values.subtitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Short description below the question"
              className="w-full"
            />
            {formik.touched.subtitle && formik.errors.subtitle && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.subtitle}
              </p>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  handleAddTag();
                }}
                placeholder="Type a tag and press Enter"
                className="flex-1"
              />
              <CommonButton
                type="default"
                disabled={!tagInput.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  handleAddTag();
                }}
              >
                <AiOutlinePlus/>
                <span>Add</span>
              </CommonButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <Tag
                  key={tag}
                  closable
                  onClose={() => handleRemoveTag(tag)}
                  className="text-sm px-3 py-1"
                  color="blue"
                >
                  {tag}
                </Tag>
              ))}
            </div>
            {formik.touched.tags && formik.errors.tags && tags.length === 0 && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.tags}
              </p>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              showTime
              value={
                formik.values.end_date
                  ? dayjs(formik.values.end_date)
                  : null
              }
              onChange={(date) => {
                formik.setFieldValue(
                  "end_date",
                  date ? date.toDate() : null
                );
              }}
              disabledDate={(current) =>
                current && !current.isAfter(dayjs(), "day")
              }
              className="w-full"
              format="YYYY-MM-DD HH:mm"
              placeholder="Select end date & time"
            />
            {formik.touched.end_date && formik.errors.end_date && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.end_date}
              </p>
            )}
          </div>

          {/* Options */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Options <span className="text-red-500">*</span>
              <span className="text-gray-400 text-xs ml-2">
                (min 2, max 5, max {MAX_OPTION_LENGTH} chars each)
              </span>
            </label>
            <div className="space-y-2">
              {formik.values.options.map((option, index) => (
                  <div key={index}>
                    <div className="flex items-center gap-2">
                      <Input
                        value={option}
                        onChange={(e) =>
                          handleOptionChange(index, e.target.value)
                        }
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                        maxLength={MAX_OPTION_LENGTH}
                        status={getOptionInputStatus(index, option)}
                      />
                      {formik.values.options.length > 2 && (
                        <span
                          className="cursor-pointer text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveOption(index)}
                        >
                          <AiOutlineClose size={18} />
                        </span>
                      )}
                    </div>
                  </div>
              ))}
            </div>
            {shouldShowOptionsError && (
              <p className="text-red-500 text-xs mt-1">
                {getOptionsErrorMessage()}
              </p>
            )}
            {formik.values.options.length < MAX_OPTIONS && (
              <CommonButton
                type="dashed"
                onClick={(e) => {
                  e.preventDefault();
                  handleAddOption();
                }}
                className="mt-3"
              >
                <AiOutlinePlus className="mr-1" /> Add Option
              </CommonButton>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <CommonButton onClick={onCancel} className="rounded-md px-6">
            Cancel
          </CommonButton>
          <CommonButton
            type="primary"
            onClick={handleSaveDraft}
            disabled={loading || publishing}
            className="rounded-md px-6 bg-[#4f46e5] hover:bg-[#4338ca]"
          >
            {loading
              ? "Saving..."
              : isEdit
              ? "Update Draft"
              : "Save as Draft"}
          </CommonButton>
          {onPublish && (
            <CommonButton
              type="primary"
              onClick={handlePublish}
              disabled={loading || publishing}
              className="rounded-md px-6 bg-[#16a34a] hover:bg-[#15803d]"
            >
              {publishing ? "Publishing..." : "Publish"}
            </CommonButton>
          )}
        </div>
      </form>

      {publishModal && (
        <ActionsModal
          title=""
          type="save"
          isOpen={publishModal}
          onCancel={() => {
            setPublishModal(false);
            setPendingPublishPayload(null);
          }}
          onOk={() => {
            setPublishModal(false);
            if (pendingPublishPayload) {
              onPublish(pendingPublishPayload);
              setPendingPublishPayload(null);
            }
          }}
          footer={false}
          centered={true}
          closable={false}
          maskClosable={false}
          className="delete-modal"
          cancelBtnClass="cancleBtnAction !bg-gray-200 !text-gray-700 hover:!bg-gray-300 !border-gray-300"
          cancelBtnClick={() => {
            setPublishModal(false);
            setPendingPublishPayload(null);
          }}
          cancelButtonProps="Cancel"
          saveBtnClass="saveBtnAction !bg-[#16a34a] hover:!bg-[#15803d] !border-[#16a34a]"
          saveBtnClick={() => {
            setPublishModal(false);
            if (pendingPublishPayload) {
              onPublish(pendingPublishPayload);
              setPendingPublishPayload(null);
            }
          }}
          saveButtonProps="Publish"
          savebtnType={false}
          description="Once published, this prediction will be visible to users and cannot be edited or deleted."
          mainTitle="Publish Prediction?"
        />
      )}
    </div>
  );
};

export default PredictionForm;
